/**
 * Variant List Cards - Product Page Variant Selector
 * Shows all variants as cards with images, SKU, price, stock, and quantity controls
 */

if (!customElements.get('variant-list-cards')) {
  class VariantListCards extends HTMLElement {
    constructor() {
      super();
      this.variantQuantities = {};
      this.productData = null;
      this.formattedData = null;
    }

    connectedCallback() {
      this.init();
    }

    init() {
      const header = this.querySelector('.vlc-header');
      const inventory = this.querySelector('.vlc-inventory');
      if (header && inventory) header.append(inventory);

      // Parse product data from script tag
      const jsonEl = this.querySelector('script[type="application/json"]');
      if (jsonEl) {
        try {
          const data = JSON.parse(jsonEl.textContent);
          this.productData = data.product;
          this.formattedData = data.formatted;
        } catch (e) {
          console.error('Error parsing product data:', e);
        }
      }

      // Upgrade old span elements to input elements (for cached Liquid templates)
      this.upgradeSpansToInputs();

      // Initialize quantity values - start at 0 (matching HTML)
      this.querySelectorAll('.vlc-variant').forEach(variant => {
        const variantId = variant.dataset.variantId;
        this.variantQuantities[variantId] = 0;
      });

      // Bind event listeners
      this.initQuantityControls();
      this.initAddButtons();
      this.initVariantSelection();

      // Auto-select first available variant
      this.selectFirstAvailableVariant();
    }

    upgradeSpansToInputs() {
      // Convert old span.vlc-variant__qty-value to input.vlc-variant__qty-input
      // This handles cached Liquid templates that still have the old structure
      this.querySelectorAll('.vlc-variant__qty-value').forEach(span => {
        const variantId = span.dataset.variantId;
        const currentValue = parseInt(span.textContent, 10) || 0;

        const input = document.createElement('input');
        input.type = 'number';
        input.className = 'vlc-variant__qty-input';
        input.dataset.variantId = variantId;
        input.value = currentValue;
        input.min = '0';
        input.max = '999';
        input.inputMode = 'numeric';
        input.pattern = '[0-9]*';
        input.setAttribute('aria-label', '數量');

        span.parentNode.replaceChild(input, span);
      });
    }

    initVariantSelection() {
      // Click on variant card to select it (including out-of-stock variants for image preview)
      this.querySelectorAll('.vlc-variant').forEach(card => {
        card.addEventListener('click', (e) => {
          // Don't trigger if clicking on controls (buttons or input)
          if (e.target.closest('.vlc-variant__qty-btn') ||
              e.target.closest('.vlc-variant__add') ||
              e.target.closest('.vlc-variant__qty-input')) return;

          const variantId = card.dataset.variantId;
          this.selectVariant(variantId);
        });
      });
    }

    selectFirstAvailableVariant() {
      // Find first available variant card
      const firstAvailable = this.querySelector('.vlc-variant:not(.is-unavailable)');
      if (firstAvailable) {
        const variantId = firstAvailable.dataset.variantId;
        this.selectVariant(variantId, false); // Don't update URL on initial load
      }
    }

    selectVariant(variantId, updateUrl = true) {
      const variant = this.productData?.variants?.find(v => v.id == variantId);
      if (!variant) return;

      // Update visual selection
      this.querySelectorAll('.vlc-variant').forEach(card => {
        card.classList.remove('is-selected');
      });
      const selectedCard = this.querySelector(`.vlc-variant[data-variant-id="${variantId}"]`);
      if (selectedCard) {
        selectedCard.classList.add('is-selected');
      }

      // Update hidden input
      const hiddenInput = this.querySelector('.vlc-variant-input');
      if (hiddenInput) {
        hiddenInput.value = variantId;
      }

      // Update URL if requested
      if (updateUrl && history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        history.replaceState({}, '', url.toString());
      }

      // Dispatch variant change event for other components (price display, media gallery, etc.)
      // Use 'on:variant:change' to match what media-gallery listens for
      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          variant: variant,
          product: this.productData
        }
      }));

      // Also dispatch the standard Shopify variant change event
      document.dispatchEvent(new CustomEvent('variant:changed', {
        bubbles: true,
        detail: {
          variant: variant,
          sectionId: this.closest('.shopify-section')?.id?.replace('shopify-section-', '')
        }
      }));

      // Update main product price display
      this.updateMainPrice(variant);

      // Update main product image
      this.updateMainImage(variant);
    }

    updateMainImage(variant) {
      // Get the variant's featured media
      const featuredMedia = variant.featured_media || variant.featured_image;
      if (!featuredMedia) return;

      // Find the product section container
      const section = this.closest('.js-product') || this.closest('.shopify-section');
      if (!section) return;

      // Find the media-gallery component and use its setActiveMedia method
      // This is the same approach used by variant-picker.js
      const mediaGallery = section.querySelector('media-gallery');
      if (mediaGallery) {
        // Find the media item by its ID (featured_media.id for media, or id for images)
        const mediaId = featuredMedia.id;
        const variantMedia = mediaGallery.querySelector(`[data-media-id="${mediaId}"]`);

        if (variantMedia && typeof mediaGallery.setActiveMedia === 'function') {
          // Use setActiveMedia to properly update the gallery with scroll
          mediaGallery.setActiveMedia(variantMedia, true);
        } else if (variantMedia && typeof mediaGallery.customSetActiveMedia === 'function') {
          // Fallback to customSetActiveMedia if available
          mediaGallery.customSetActiveMedia(variantMedia, true);
        }
      }

      // Also update the quick-add product summary image if present
      const variantImageSrc = variant.featured_image?.src;
      const summaryImage = this.querySelector('.vlc-product-summary__image img');
      if (summaryImage && variantImageSrc) {
        const newSrc = variantImageSrc.replace(/\?.*$/, '');
        summaryImage.src = `${newSrc}?width=200`;
      }
    }

    updateMainPrice(variant) {
      // Find the price container in the product section
      const productSection = this.closest('.shopify-section');
      if (!productSection) return;

      const priceContainer = productSection.querySelector('.product-price .price, .product-info__price .price');
      if (!priceContainer) return;

      // Get formatted price from our stored data
      const formatted = this.formattedData?.[variant.id];

      // Update current price
      const priceValueEl = priceContainer.querySelector('.price__current .js-value');
      if (priceValueEl && formatted?.price) {
        priceValueEl.textContent = formatted.price;
      }

      // Update compare at price (was price)
      const compareEl = priceContainer.querySelector('.price__was .js-value');
      const hasComparePrice = variant.compare_at_price && variant.compare_at_price > variant.price;

      if (compareEl) {
        if (hasComparePrice) {
          compareEl.textContent = this.formatMoney(variant.compare_at_price);
        } else {
          compareEl.textContent = '';
        }
      }

      // Toggle sale price class
      priceContainer.classList.toggle('price--on-sale', hasComparePrice);

      // Toggle sold out class
      priceContainer.classList.toggle('price--sold-out', !variant.available);
    }

    formatMoney(cents) {
      // Shopify stores prices in smallest currency unit (cents)
      return 'NT$' + Math.round(cents / 100).toLocaleString();
    }

    initQuantityControls() {
      // Button click handlers
      this.querySelectorAll('.vlc-variant__qty-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const variantId = btn.dataset.variantId;
          const action = btn.dataset.action;

          // Select this variant to update price display
          this.selectVariant(variantId);

          this.updateQuantity(variantId, action);
        });
      });

      // Direct input handlers
      this.querySelectorAll('.vlc-variant__qty-input').forEach(input => {
        // Handle input changes
        input.addEventListener('input', (e) => {
          this.handleQuantityInput(input);
        });

        // Select all text on focus for easy editing
        input.addEventListener('focus', () => {
          input.select();
        });
      });
    }

    handleQuantityInput(input) {
      const variantId = input.dataset.variantId;
      let qty = parseInt(input.value, 10);

      // Validate the input
      if (isNaN(qty) || qty < 0) {
        qty = 0;
      }
      // Cap at reasonable maximum
      if (qty > 999) {
        qty = 999;
      }

      input.value = qty;
      this.variantQuantities[variantId] = qty;

      // Update decrease button state
      const decreaseBtn = this.querySelector(`.vlc-variant__qty-btn[data-action="decrease"][data-variant-id="${variantId}"]`);
      const qtyContainer = this.querySelector(`.vlc-variant__qty[data-variant-id="${variantId}"]`);

      if (decreaseBtn) decreaseBtn.disabled = qty <= 0;

      // Toggle is-active class based on quantity
      if (qtyContainer) {
        if (qty > 0) {
          qtyContainer.classList.add('is-active');
        } else {
          qtyContainer.classList.remove('is-active');
        }
      }

      // Select this variant to update price display
      this.selectVariant(variantId);
    }

    updateQuantity(variantId, action) {
      const inputEl = this.querySelector(`.vlc-variant__qty-input[data-variant-id="${variantId}"]`);
      const decreaseBtn = this.querySelector(`.vlc-variant__qty-btn[data-action="decrease"][data-variant-id="${variantId}"]`);
      const qtyContainer = this.querySelector(`.vlc-variant__qty[data-variant-id="${variantId}"]`);

      // Default to 0, not 1
      let qty = this.variantQuantities[variantId] || 0;

      if (action === 'increase') {
        qty++;
      } else if (action === 'decrease' && qty > 0) {
        qty--;
      }

      this.variantQuantities[variantId] = qty;
      if (inputEl) inputEl.value = qty;
      if (decreaseBtn) decreaseBtn.disabled = qty <= 0;

      // Toggle is-active class based on quantity
      if (qtyContainer) {
        if (qty > 0) {
          qtyContainer.classList.add('is-active');
        } else {
          qtyContainer.classList.remove('is-active');
        }
      }
    }

    initAddButtons() {
      this.querySelectorAll('.vlc-variant__add').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.addToCart(btn);
        });
      });
    }

    async addToCart(btn) {
      const variantId = btn.dataset.variantId;
      const qty = this.variantQuantities[variantId] || 0;

      // Don't add if quantity is 0
      if (qty <= 0) {
        // Auto-set to 1 and add
        this.variantQuantities[variantId] = 1;
        const inputEl = this.querySelector(`.vlc-variant__qty-input[data-variant-id="${variantId}"]`);
        const qtyContainer = this.querySelector(`.vlc-variant__qty[data-variant-id="${variantId}"]`);
        if (inputEl) inputEl.value = 1;
        if (qtyContainer) qtyContainer.classList.add('is-active');
      }

      const finalQty = this.variantQuantities[variantId] || 1;
      const variantCard = btn.closest('.vlc-variant');

      // Select this variant to update price display
      this.selectVariant(variantId);

      btn.classList.add('is-loading');

      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', finalQty);

        // Always request cart-icon-bubble section for header badge update
        let sectionsToRequest = ['cart-icon-bubble'];

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          const cartSection = cartDrawer.closest('.shopify-section');
          if (cartSection) {
            const sectionId = cartSection.id.replace('shopify-section-', '');
            sectionsToRequest.push(sectionId);
          }
        }

        formData.append('sections', sectionsToRequest.join(','));
        formData.append('sections_url', window.location.pathname);

        const response = await fetch('/cart/add', {
          method: 'POST',
          headers: {
            'Accept': 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Failed to add to cart');
        }

        // Update the drawer, icon bubble, and dispatch a consistent
        // completion event (on:cart:change + legacy cart:updated) — see
        // window.cartDrawerCoordinator.applyCartUpdate in cart-drawer-coordinator.js.
        if (window.cartDrawerCoordinator) {
          window.cartDrawerCoordinator.applyCartUpdate(data, { openDrawer: true });
        }

        // Show success state
        this.showAddedState(btn, variantCard);

        document.dispatchEvent(new CustomEvent('cart:item-added', {
          detail: {
            title: data.product_title || data.title,
            variantTitle: data.variant_title,
            image: data.image || (data.featured_image && data.featured_image.url),
            price: data.final_price,
            quantity: finalQty,
            url: data.url
          }
        }));

      } catch (error) {
        console.error('Error adding to cart:', error);
        document.dispatchEvent(new CustomEvent('cart:add-error', {
          detail: { message: error.message || '無法加入購物車' }
        }));
      } finally {
        btn.classList.remove('is-loading');
      }
    }

    showAddedState(btn, variantCard) {
      const controls = btn.closest('.vlc-variant__controls');
      const variantId = btn.dataset.variantId;

      // Add visual feedback to card
      variantCard.classList.add('is-added');

      // Replace button with checkmark
      btn.outerHTML = `
        <div class="vlc-variant__added">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>
      `;

      // Revert after 2 seconds
      setTimeout(() => {
        variantCard.classList.remove('is-added');
        const addedEl = controls.querySelector('.vlc-variant__added');
        if (addedEl) {
          addedEl.outerHTML = `
            <button type="button" class="vlc-variant__add" data-variant-id="${variantId}" title="加入購物車" aria-label="加入購物車">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 5v14M5 12h14"/>
              </svg>
            </button>
          `;
          // Rebind event
          const newBtn = controls.querySelector('.vlc-variant__add');
          if (newBtn) {
            newBtn.addEventListener('click', (e) => {
              e.preventDefault();
              this.addToCart(newBtn);
            });
          }
        }
      }, 2000);
    }

  }

  customElements.define('variant-list-cards', VariantListCards);
}
